/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import {
  fetchDepartments,
  toggleDepartmentStatus,
} from "store/features/company/department/departmentActions";
import {
  selectDepartments,
  selectDepartmentLoading,
} from "store/features/company/department/departmentSelectors";
import AddDepartmentModal from "./AddDepartmentModal";
import EditDepartmentModal from "./EditDepartmentModal";
import departmentsSvg from "assets/folders/departments.svg";

const Department = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const departments = useSelector(selectDepartments);
  const loading = useSelector(selectDepartmentLoading);

  const [togglingStatus, setTogglingStatus] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);
  const handleStatusToggle = async (department) => {
    setTogglingStatus(true);
    const result = await dispatch(
      toggleDepartmentStatus(department.id, department.status),
    );
    if (result.success) {
    }
    setTogglingStatus(false);
  };
  const filteredDepartments = departments.filter((department) => {
    const matchesSearch =
      searchTerm === "" ||
      department.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      department.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      department.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || department.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatBudget = (budget) => {
    if (!budget) return "₹0";
    const num = parseFloat(budget);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={departmentsSvg}
              alt="Departments"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Departments
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage your organization departments
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Department
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by name, code or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "#f59e0b",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading departments...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    ID / Tenant ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Code
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Budget
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",

                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Created At
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDepartments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <BusinessIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        }}
                      >
                        No departments found. Click "Add Department" to get
                        started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDepartments.map((department, index) => (
                    <TableRow
                      key={department.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#bbbfc8"
                                  : "#53555a",
                            }}
                          >
                            #{department.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#6b7280"
                                  : "#9ca3af",
                            }}
                          >
                            <b>Tenant: </b> {department.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon
                            sx={{ fontSize: 20, color: "#f59e0b" }}
                          />
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {department.code}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                              fontSize: "0.875rem",
                            }}
                          >
                            {department.name}
                          </Typography>
                          {department.parent_department_id && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#9ca3af"
                                    : "#6b7280",
                              }}
                            >
                              <b>Parent Dept ID: </b>{" "}
                              {department.parent_department_id}
                            </Typography>
                          )}
                          {department.hod_id && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#9ca3af"
                                    : "#6b7280",
                              }}
                            >
                              HOD ID: {department.hod_id}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {formatBudget(department.budget)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color:
                              theme.palette.mode === "dark"
                                ? "#d1d5db"
                                : "#4b5563",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={department.description}
                        >
                          {department.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          onClick={() => handleStatusToggle(department)}
                          sx={{
                            display: "inline-flex",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#e5e7eb",
                            borderRadius: "40px",
                            padding: "2px",
                            cursor: "pointer",
                            width: "fit-content",
                            minWidth: "140px",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              px: 2,
                              py: 0.5,
                              borderRadius: "40px",
                              backgroundColor:
                                department.status === "active"
                                  ? theme.palette.mode === "dark"
                                    ? "#10b981"
                                    : "#d1fae5"
                                  : "transparent",
                              color:
                                department.status === "active"
                                  ? theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#065f46"
                                  : theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              textAlign: "center",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                            Active
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                              px: 2,
                              py: 0.5,
                              borderRadius: "40px",
                              backgroundColor:
                                department.status === "inactive"
                                  ? theme.palette.mode === "dark"
                                    ? "#ef4444"
                                    : "#fee2e2"
                                  : "transparent",
                              color:
                                department.status === "inactive"
                                  ? theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#991b1b"
                                  : theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              textAlign: "center",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 14 }} />
                            Inactive
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {formatDateTime(department.created_at)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => {
                            setSelectedDepartment(department);
                            setShowEditModal(true);
                          }}
                          size="small"
                          sx={{
                            color: "#f59e0b",
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(245,158,11,0.2)"
                                  : "#fff5ce",
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Department Count Summary */}
      {!loading && departments.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Departments: {departments.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredDepartments.length} of {departments.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredDepartments.length === 0 &&
        departments.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                mb: 2,
              }}
            >
              No departments match your search criteria
            </Typography>
            <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              sx={{
                color: "#f59e0b",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Modals */}
      <AddDepartmentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchDepartments())}
      />

      <EditDepartmentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDepartment(null);
        }}
        department={selectedDepartment}
        onSuccess={() => dispatch(fetchDepartments())}
      />
    </Box>
  );
};

export default Department;
